import type { AccessRight } from "../shared/auth/accessRights";
import { hasAccessRight } from "../shared/auth/accessRights";
import type { MessageKey } from "../shared/i18n/messages.de";
import type { IconName } from "../shared/ui/Icon";

/** Application routes. */
export const routePaths = {
  clock: "/",
  times: "/times",
  balances: "/balances",
  corrections: "/corrections",
  absences: "/absences",
  team: "/team",
  violations: "/violations",
  projects: "/projects",
  roster: "/roster",
  myRoster: "/my-roster",
  setup: "/setup",
  admin: "/admin",
  privacy: "/privacy",
  install: "/install",
  tenant: "/tenant",
  billing: "/billing",
  more: "/more",
  /** Preferences, notifications of this device and the account; opened from the account menu (F-74). */
  settings: "/settings",
  loginCallback: "/auth/callback",
  signup: "/signup",
  signupVerify: "/signup/verify",
  /** Screen of a shared terminal device (T-173); outside the sign-in of persons. */
  terminal: "/terminal",
} as const;

/** An entry of the main navigation. */
export interface NavigationItem {
  /** Route path. */
  readonly path: string;
  /** Label message key. */
  readonly labelKey: MessageKey;
  /**
   * Label in the navigation, if it differs from the page title: the German bar labels carry soft
   * hyphens, so long words break at a syllable in the narrow bar (accessible names ignore them).
   */
  readonly navLabelKey?: MessageKey;
  /** Icon shown next to the label. */
  readonly icon: IconName;
  /**
   * Access right required to see the entry, a list of rights of which one is enough, or `null` for
   * everybody.
   */
  readonly requires: AccessRight | readonly AccessRight[] | null;
  /**
   * Rank for a place in the bottom navigation bar of phones (lower first), or `null` if the entry
   * is reached through "Mehr" there.
   */
  readonly barRank: number | null;
  /**
   * Rostering the entry needs besides the rights (F-62): `active` while the tenant has the
   * entitlement and switched it on, `plans` while the person also plans or is HR.
   */
  readonly rostering?: "active" | "plans";
  /**
   * Shows the entry also to project responsibles without its right who are responsible for at least
   * one project (T-125, F-74): wide screens have no „Mehr“ that would link the page otherwise.
   */
  readonly projectResponsibles?: true;
}

/** Destinations of the phone navigation bar besides "Mehr" (Material 3 and HIG: three to five). */
export const maxBarDestinations = 4;

/**
 * Media query of the wide layout (navigation drawer on the left, no „Mehr“, F-74); the same
 * breakpoint as `AppLayout.module.css`.
 */
export const wideLayoutQuery = "(min-width: 720px)";

/** The navigation split for the phone layout. */
export interface NavigationLayout {
  /** Entries in the navigation bar, in display order. */
  readonly bar: readonly NavigationItem[];
  /** Entries reached through "Mehr", in display order. */
  readonly overflow: readonly NavigationItem[];
}

/** All navigation entries in display order. */
export const navigationItems: readonly NavigationItem[] = [
  {
    path: routePaths.clock,
    labelKey: "nav.clock",
    icon: "clock",
    requires: "AccessOwnRecords",
    barRank: 0,
  },
  {
    path: routePaths.times,
    labelKey: "nav.times",
    icon: "times",
    requires: "AccessOwnRecords",
    barRank: 1,
  },
  {
    path: routePaths.balances,
    labelKey: "nav.balances",
    icon: "balances",
    requires: "AccessOwnRecords",
    barRank: 4,
  },
  {
    path: routePaths.corrections,
    labelKey: "nav.corrections",
    icon: "corrections",
    requires: "AccessOwnRecords",
    barRank: null,
  },
  {
    path: routePaths.absences,
    labelKey: "nav.absences",
    navLabelKey: "nav.bar.absences",
    icon: "absences",
    requires: "AccessOwnRecords",
    barRank: 2,
  },
  {
    path: routePaths.myRoster,
    labelKey: "nav.myRoster",
    icon: "roster",
    requires: "AccessOwnRecords",
    barRank: null,
    rostering: "active",
  },
  {
    path: routePaths.team,
    labelKey: "nav.team",
    icon: "team",
    requires: "ApproveCorrections",
    barRank: 3,
  },
  {
    path: routePaths.violations,
    labelKey: "nav.violations",
    icon: "violations",
    requires: "ManageViolations",
    barRank: null,
  },
  {
    path: routePaths.projects,
    labelKey: "nav.projects",
    icon: "projects",
    requires: "ManageProjects",
    barRank: null,
    projectResponsibles: true,
  },
  {
    path: routePaths.roster,
    labelKey: "nav.roster",
    icon: "rosterPlanning",
    requires: "AccessOwnRecords",
    barRank: null,
    rostering: "plans",
  },
  {
    path: routePaths.setup,
    labelKey: "nav.setup",
    icon: "setup",
    requires: "ManageMasterData",
    barRank: null,
  },
  {
    path: routePaths.admin,
    labelKey: "nav.admin",
    icon: "admin",
    requires: "ManageMasterData",
    barRank: null,
  },
  {
    path: routePaths.tenant,
    labelKey: "nav.tenant",
    icon: "tenant",
    // HR administrators maintain the leave rules under „Mandant → Allgemein“ (T-161).
    requires: ["ManageTenant", "ManageMasterData"],
    barRank: null,
  },
  {
    path: routePaths.billing,
    labelKey: "nav.billing",
    icon: "billing",
    requires: "ReadSubscription",
    barRank: null,
  },
  {
    path: routePaths.privacy,
    labelKey: "nav.privacy",
    icon: "privacy",
    requires: null,
    barRank: null,
  },
  {
    path: routePaths.install,
    labelKey: "nav.install",
    icon: "install",
    requires: null,
    barRank: null,
  },
];

/** Circumstances besides the roles that hide navigation entries. */
export interface NavigationContext {
  /** Whether the app runs installed; hides the installation hint. */
  readonly installed: boolean;
  /** Whether ways to buy may be shown; `false` in the native apps hides billing (ADR-0087). */
  readonly purchasePaths: boolean;
  /**
   * Rostering of the person (`GET /me/rostering`, F-62); missing or unknown hides the rostering
   * entries.
   */
  readonly rostering?: { readonly active: boolean; readonly plans: boolean } | undefined;
  /**
   * Whether the person is responsible for at least one project without the right to manage
   * projects (T-125); shows the entries marked `projectResponsibles`.
   */
  readonly projectResponsible?: boolean | undefined;
}

/** The browser before installation: every entry the roles allow. */
const browserNavigation: NavigationContext = { installed: false, purchasePaths: true };

/**
 * Filters the navigation entries by the roles of the person. The installation hint disappears
 * once the app runs installed; billing never appears in the native apps; „Projekte“ also appears for
 * project responsibles (T-125, F-74).
 *
 * @param roles - Roles from `GET /me`.
 * @param context - Installation and platform; the browser before installation by default.
 * @returns The visible entries in display order.
 */
export function visibleNavigation(
  roles: readonly string[],
  context: NavigationContext = browserNavigation,
): readonly NavigationItem[] {
  const visible = navigationItems.filter(
    (item) =>
      (isNavigationAllowed(roles, item) ||
        (item.projectResponsibles === true && context.projectResponsible === true)) &&
      !(context.installed && item.path === routePaths.install) &&
      (context.purchasePaths || item.path !== routePaths.billing) &&
      (item.rostering === undefined || context.rostering?.[item.rostering] === true),
  );

  return visible;
}

/**
 * Returns the access rights of which one shows an entry.
 *
 * @param item - The entry.
 * @returns The rights; empty for an entry everybody sees.
 */
export function requiredRights(item: NavigationItem): readonly AccessRight[] {
  const rights: readonly AccessRight[] =
    item.requires === null
      ? []
      : typeof item.requires === "string"
        ? [item.requires]
        : item.requires;

  return rights;
}

/**
 * Returns whether the roles show an entry: it needs no right or one of its rights is granted.
 *
 * @param roles - Roles of the person.
 * @param item - The entry.
 * @returns `true` if the entry may be shown.
 */
export function isNavigationAllowed(roles: readonly string[], item: NavigationItem): boolean {
  const rights = requiredRights(item);
  const allowed = rights.length === 0 || rights.some((right) => hasAccessRight(roles, right));

  return allowed;
}

/**
 * Splits the visible entries for phones: the entries with the best rank go into the navigation
 * bar (at most {@link maxBarDestinations}), all others are reached through "Mehr". Managers get
 * "Team" instead of "Salden", because they open it daily. Roles without own records (tenant
 * administrators, auditors) fill the bar with their first entries, so it never stays empty.
 *
 * @param visible - The visible entries in display order.
 * @returns The bar and overflow entries, both in display order.
 */
export function layoutNavigation(visible: readonly NavigationItem[]): NavigationLayout {
  const ranked = visible
    .filter((item) => item.barRank !== null)
    .toSorted((first, second) => (first.barRank ?? 0) - (second.barRank ?? 0));
  const unranked = visible.filter((item) => item.barRank === null);
  const candidates = [...ranked, ...unranked].slice(0, maxBarDestinations);
  const barPaths = new Set(candidates.map((item) => item.path));
  const bar = visible.filter((item) => barPaths.has(item.path));
  const overflow = visible.filter((item) => !barPaths.has(item.path));
  const layout: NavigationLayout = { bar, overflow };

  return layout;
}

/**
 * Pages „Mehr“ stands for while their own navigation entries are hidden, for example while the
 * rostering of the person is not known yet (T-176, T-178, F-62).
 */
const onlyBelowMore: readonly string[] = [routePaths.myRoster, routePaths.roster];

/**
 * Returns whether a page is reached through „Mehr“: an entry that does not fit into the navigation
 * (the overflow of the phone bar) or a page only „Mehr“ links to. „Mehr“ then shows the active
 * indicator, so the navigation still says where the person is, as the Material 3 navigation bar
 * does for its overflow (F-57). „Mehr“ itself is marked by its own link.
 *
 * @param pathname - Path of the current page.
 * @param overflow - Entries reached through „Mehr“ in the layout at hand; none on wide screens.
 * @param visible - The visible entries; a page with its own entry is no page „Mehr“ stands for.
 * @returns `true` for a page below „Mehr“.
 */
export function isBelowMore(
  pathname: string,
  overflow: readonly NavigationItem[],
  visible: readonly NavigationItem[] = [],
): boolean {
  const ownEntries = new Set(visible.map((item) => item.path));
  const paths = [
    ...overflow.map((item) => item.path),
    ...onlyBelowMore.filter((path) => !ownEntries.has(path)),
  ];
  const below = paths.some((path) => pathname === path || pathname.startsWith(`${path}/`));

  return below;
}
